/**
 * CareMap geographic targeting — country + subdivision lists (Kenya counties; Spain / Thailand provinces from Google Ads geo targets).
 * Keep in sync with `functions/functions/src/ads/localCarousel/gtgGeoShared.js` for Cloud Functions.
 */

export {
  GTG_SPAIN_PROVINCES,
  GTG_THAILAND_PROVINCES,
} from "./gtgSpainThailandProvincesConstants";

/** @typedef {'US' | 'KE' | 'ES' | 'TH'} GtgTargetCountry */

export const GTG_TARGET_COUNTRY_US = "US";

export const GTG_TARGET_COUNTRY_KE = "KE";

export const GTG_TARGET_COUNTRY_ES = "ES";

export const GTG_TARGET_COUNTRY_TH = "TH";

/** @type {GtgTargetCountry[]} */
export const GTG_TARGET_COUNTRIES = [
  GTG_TARGET_COUNTRY_US,
  GTG_TARGET_COUNTRY_KE,
  GTG_TARGET_COUNTRY_ES,
  GTG_TARGET_COUNTRY_TH,
];

export const GTG_TARGET_COUNTRY_LABELS = {
  [GTG_TARGET_COUNTRY_US]: "United States",
  [GTG_TARGET_COUNTRY_KE]: "Kenya",
  [GTG_TARGET_COUNTRY_ES]: "Spain",
  [GTG_TARGET_COUNTRY_TH]: "Thailand",
};

/**
 * @param {unknown} v
 * @returns {GtgTargetCountry}
 */
export function normalizeGtgTargetCountry(v) {
  if (v === GTG_TARGET_COUNTRY_KE) {
    return GTG_TARGET_COUNTRY_KE;
  }
  if (v === GTG_TARGET_COUNTRY_ES) {
    return GTG_TARGET_COUNTRY_ES;
  }
  if (v === GTG_TARGET_COUNTRY_TH) {
    return GTG_TARGET_COUNTRY_TH;
  }
  return GTG_TARGET_COUNTRY_US;
}

/**
 * Non–US groups use a canonical subdivision list stored in Firestore as `kenyaCounties` (shared field).
 * @param {unknown} c
 * @returns {boolean}
 */
export function gtgTargetCountryUsesSubdivisionList(c) {
  const x = normalizeGtgTargetCountry(c);
  return (
    x === GTG_TARGET_COUNTRY_KE ||
    x === GTG_TARGET_COUNTRY_ES ||
    x === GTG_TARGET_COUNTRY_TH
  );
}

/**
 * @param {unknown} c
 * @returns {"counties" | "provinces"}
 */
export function gtgSubdivisionPluralNoun(c) {
  const x = normalizeGtgTargetCountry(c);
  return x === GTG_TARGET_COUNTRY_KE ? "counties" : "provinces";
}

/**
 * @param {unknown} c
 * @returns {"county" | "province"}
 */
export function gtgSubdivisionSingularNoun(c) {
  const x = normalizeGtgTargetCountry(c);
  return x === GTG_TARGET_COUNTRY_KE ? "county" : "province";
}

/**
 * User-facing hint for validation errors (unknown subdivision names).
 * @param {unknown} c
 * @returns {string}
 */
export function gtgSubdivisionReferenceHint(c) {
  const x = normalizeGtgTargetCountry(c);
  if (x === GTG_TARGET_COUNTRY_KE) {
    return "Healthcare ad rate reference (Kenya counties)";
  }
  if (x === GTG_TARGET_COUNTRY_ES) {
    return "Google Ads province names for Spain";
  }
  if (x === GTG_TARGET_COUNTRY_TH) {
    return "Google Ads province names for Thailand";
  }
  return "";
}

/**
 * Kenya counties — same canonical spellings as the Healthcare ad rate reference.
 * @type {readonly string[]}
 */
export const GTG_KENYA_COUNTIES = Object.freeze([
  "Baringo",
  "Bomet",
  "Bungoma",
  "Busia",
  "Elgeyo-Marakwet",
  "Embu",
  "Garissa",
  "Homa Bay",
  "Isiolo",
  "Kajiado",
  "Kakamega",
  "Kericho",
  "Kiambu",
  "Kilifi",
  "Kirinyaga",
  "Kisii",
  "Kisumu",
  "Kitui",
  "Kwale",
  "Laikipia",
  "Lamu",
  "Machakos",
  "Makueni",
  "Mandera",
  "Marsabit",
  "Meru",
  "Migori",
  "Mombasa",
  "Murang'a",
  "Nairobi",
  "Nakuru",
  "Nandi",
  "Narok",
  "Nyamira",
  "Nyandarua",
  "Nyeri",
  "Samburu",
  "Siaya",
  "Taita-Taveta",
  "Tana River",
  "Tharaka-Nithi",
  "Trans Nzoia",
  "Turkana",
  "Uasin Gishu",
  "Vihiga",
  "Wajir",
  "West Pokot",
]);
